import type { FormatInfo, MediaType } from "./types";

export function formatDuration(seconds: number | null): string {
  if (!Number.isFinite(seconds) || seconds === null || seconds < 0) return "";
  const rounded = Math.floor(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remaining = rounded % 60;
  const values = hours > 0 ? [hours, minutes, remaining] : [minutes, remaining];
  return values.map((value) => String(value).padStart(2, "0")).join(":");
}

export function formatBytes(bytes: number | null): string {
  if (!Number.isFinite(bytes) || bytes === null || bytes <= 0) return "Unknown";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** index;
  return value.toFixed(index >= 2 ? 1 : 0) + " " + units[index];
}

export function formatEta(seconds: number | null): string {
  if (!Number.isFinite(seconds) || seconds === null || seconds < 0) return "";
  if (seconds < 60) return Math.ceil(seconds) + " sec left";
  const minutes = Math.ceil(seconds / 60);
  return minutes + (minutes === 1 ? " min left" : " mins left");
}

export function qualityLabel(format: FormatInfo): string {
  if (format.height) {
    const fps = format.fps && format.fps > 30 ? " · " + Math.round(format.fps) + " fps" : "";
    return format.height + "p" + fps;
  }
  if (format.quality_note) return format.quality_note;
  if (format.media_type === "audio" && format.bitrate_kbps) {
    return Math.round(format.bitrate_kbps) + " kbps";
  }
  return format.media_type === "audio" ? "Audio" : "Standard";
}

export function mediaTypeLabel(mediaType: MediaType): string {
  const labels: Record<MediaType, string> = {
    "video+audio": "Video + audio",
    video: "Video only",
    audio: "Audio only",
    unknown: "Media",
  };
  return labels[mediaType];
}

export function formatLabel(format: FormatInfo): string {
  const extension = (format.extension || "media").toUpperCase();
  const protocol = String(format.protocol || "").toLowerCase();
  if (protocol.includes("m3u8")) return extension + " · HLS";
  if (protocol.includes("dash") || protocol.includes("mpd")) return extension + " · DASH";
  return extension;
}

export function isManifestFormat(format: FormatInfo): boolean {
  const protocol = String(format.protocol || "").toLowerCase();
  const url = format.url || "";
  return (
    protocol.includes("m3u8") ||
    protocol.includes("dash") ||
    protocol.includes("ism") ||
    /\.m3u8(?:$|[?#])/i.test(url) ||
    /\.mpd(?:$|[?#])/i.test(url)
  );
}

export function sortFormats(formats: FormatInfo[]): FormatInfo[] {
  const mediaRank: Record<MediaType, number> = {
    "video+audio": 4,
    video: 3,
    audio: 2,
    unknown: 1,
  };
  return [...formats].sort((left, right) => {
    const heightDifference = (right.height || 0) - (left.height || 0);
    if (heightDifference !== 0) return heightDifference;
    const mediaDifference = mediaRank[right.media_type] - mediaRank[left.media_type];
    if (mediaDifference !== 0) return mediaDifference;
    return (right.bitrate_kbps || 0) - (left.bitrate_kbps || 0);
  });
}

